import { DefaultOptionType, SelectProps } from 'antd/es/select'
import { useTranslation } from 'react-i18next'

import {
  SimpleUserEntity,
  UserBookEntity,
  UserEntity,
  UserFindManyQueryParams,
  useUserAdminFindMany,
  useUserBookFindMany,
  useUserFindMany,
} from '@/api'
import { KBFlex } from '@/components/atoms'
import { KBSelect, KBSelectProps } from '@/components/atoms/KBSelect'
import { KBTag } from '@/components/atoms/KBTag'
import { KBUser } from '@/components/organisms/KBUser/KBUser'
import { CACHE_TTL, MAX_PAGINATION_LIMIT } from '@/utils/constants'
import { cn } from '@/utils/tailwind.helper'
import { isUserClient } from '@/utils/userEnvironment'
import { debounce, unique } from 'radash'

type TagRender = SelectProps['tagRender']

export interface KBUserSelectProps extends KBSelectProps {
  usersQueryParams?: UserFindManyQueryParams
  /**
   * 包含，可以控制选择内部和外部联系人，默认包含内部联系人
   */
  includes?: 'internal' | 'external' | 'internal,external'
  enableDeliveryNicknameSearch?: boolean // 新增属性,是否对快递昵称进行模糊查询
  showNickname?: boolean // 新增属性,是否显示快递昵称
  // 新增一个属性，控制是否允许在回车时添加新选项
  enableAddNewOptionOnEnter?: boolean
  userBookStaleTime?: number
}

export function getUserSelectOptions(
  users: SimpleUserEntity[],
  mode?: 'multiple' | 'tags',
  showNickname?: boolean
): DefaultOptionType[] {
  const isMultiple = mode && ['multiple', 'tags'].includes(mode)
  return users.map((user) => ({
    label: (
      // select为单选模式时，上下会有几px的差距，所有这里包一层
      <KBFlex>
        <KBUser
          key={user.id}
          size={32}
          user={user}
          showTooltip={false}
          gapNumber={isMultiple ? 4 : 8}
          showEmail={true}
          showNickname={showNickname}
          className="text-select-option tw-py-1"
          // showDepartment
        />
      </KBFlex>
    ),

    value: user.id,
    user: user,
  }))
}

export const tagRender: TagRender = (props) => {
  const { label, closable, onClose } = props
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }
  return (
    <KBTag
      className="tw-my-0.5 tw-h-6 tw-rounded-full tw-py-1 tw-pl-1.5 tw-pr-2 "
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      bordered={false}
    >
      {typeof label === 'string' ? label.replace('...', '') : label}
    </KBTag>
  )
}

/**
 * 选择人员组件，默认过滤离职和未激活的人员，可以传入state覆盖默认值active
 */
export function KBUserSelect(props: KBUserSelectProps) {
  const { t } = useTranslation()
  const {
    className,
    mode,
    remoteSearch = true,
    placeholder = t('common.userSelectPlaceholder'),
    allowClear = true,
    usersQueryParams = {},
    includes = 'internal',
    enableDeliveryNicknameSearch = false, // 默认值为 false
    showNickname,
    options,
    onChange,
    enableAddNewOptionOnEnter = true,
    userBookStaleTime,
    ...otherProps
  } = props
  const [search, setSearch] = useState('')
  // 防止多次搜索，因为setSearch之后会重新render，导致多触发一次onSearch
  const [queryKey, setQueryKey] = useState('')
  const [internalIds, setInternalIds] = useState<number[]>([])
  const [externalIds, setExternalIds] = useState<number[]>([])
  const [userOptions, setUserOptions] = useState<DefaultOptionType[]>(
    options || []
  )
  const errorIds = useRef<number[]>([])
  const isMultiple = mode && ['multiple', 'tags'].includes(mode)
  const [selectedUsers, setSelectedUsers] = useState<SimpleUserEntity[]>([])

  const internalResponse = isUserClient()
    ? useUserFindMany(
        {
          state: 'active',
          ...usersQueryParams,
          search_term: search || undefined,
          delivery_nickname:
            enableDeliveryNicknameSearch && search ? search : undefined,
          ids: internalIds,
          limit: MAX_PAGINATION_LIMIT,
        },
        {
          query: {
            enabled: false,
            staleTime: CACHE_TTL,
          },
        }
      )
    : useUserAdminFindMany(
        {
          state: 'active',
          ...usersQueryParams,
          search_term: search || undefined,
          delivery_nickname:
            enableDeliveryNicknameSearch && search ? search : undefined,
          ids: internalIds,
          limit: MAX_PAGINATION_LIMIT,
        },
        {
          query: {
            enabled: false,
            staleTime: CACHE_TTL,
          },
        }
      )

  const externalResponse = useUserBookFindMany(
    {
      search_field: search || undefined,
      ids: externalIds.map((id) => 0 - id),
      limit: MAX_PAGINATION_LIMIT,
    },
    { query: { enabled: false, staleTime: userBookStaleTime || CACHE_TTL } }
  )

  useEffect(() => {
    if (!remoteSearch) return
    let loadedLength = 0
    const resultArray: DefaultOptionType[] = []

    if (includes.includes('internal') && internalResponse?.data) {
      loadedLength++
      resultArray.push(
        ...getUserSelectOptions(
          internalResponse?.data?.result || [],
          mode,
          showNickname
        )
      )
    }

    if (includes.includes('external') && externalResponse?.data) {
      loadedLength++
      resultArray.push(
        ...getUserSelectOptions(
          (externalResponse?.data?.result || []).map((i: UserBookEntity) => ({
            ...i,
            // 为了防止id冲突，暂时把外部的id改成负数
            id: 0 - i.id,
          })),
          mode,
          showNickname
        )
      )
    }

    let isLoadAll = loadedLength === includes.split(',').length
    // 当同时支持外部和内部，但只需要搜索一个时
    if (
      ((internalIds.length && !externalIds.length) ||
        (!internalIds.length && externalIds.length)) &&
      includes.split(',').length === 2 &&
      loadedLength === 1
    ) {
      isLoadAll = true
    }

    if (remoteSearch && isLoadAll) {
      const mergedOptions = unique(
        [
          ...getUserSelectOptions(selectedUsers, mode, showNickname),
          ...resultArray,
          // 初始 options(编辑回填时由父组件用预定自带的 attendee 信息构造)兜底:
          // 外部联系人 user_book 按 host_user_id 私有隔离, 非创建者编辑时远程查不到,
          // 放最后(同 value 优先用远程结果), 保证回填项至少有 label 不退化成裸 id。
          ...(props.options || []),
        ],
        (item) => item.value!
      )
      // 标记没有对应的id，防止重复请求
      errorIds.current = [
        ...errorIds.current,
        ...internalIds.filter(
          (id) => !mergedOptions.some((option) => option.value === id)
        ),
        ...externalIds.filter(
          (id) => !mergedOptions.some((option) => option.value === id)
        ),
      ]

      setUserOptions(mergedOptions)
      if (internalIds.length !== 0) {
        setInternalIds([])
      }
      if (externalIds.length !== 0) {
        setExternalIds([])
      }
      if (Array.isArray(props.value)) {
        setSelectedUsers(
          mergedOptions
            .filter((option) => props.value.includes(option.value as number))
            .map((option) => option.user)
        )
      } else if (typeof props.value === 'number') {
        const userData = mergedOptions.find(
          (user) => user.value === props.value
        )
        if (userData) {
          setSelectedUsers([userData.user])
        }
      }
    }
  }, [
    mode,
    showNickname,
    remoteSearch,
    internalResponse.data,
    externalResponse.data,
    internalIds,
    externalIds,
    includes,
    props.value,
    props.options,
  ])

  const handleRemoteSearch = useCallback(
    debounce({ delay: 500 }, (value: string) => {
      if (value !== search && value.trim().length > 0) {
        setSearch(value)
      }
    }),
    [search]
  )

  useEffect(() => {
    if (remoteSearch && search && search !== queryKey) {
      setQueryKey(search)
      if (includes.includes('internal')) {
        internalResponse.refetch()
      }
      if (includes.includes('external')) {
        externalResponse.refetch()
      }
    }
  }, [
    remoteSearch,
    search,
    queryKey,
    includes,
    internalResponse,
    externalResponse,
  ])

  // Load initial data when component mounts (for empty search)
  useEffect(() => {
    if (remoteSearch && userOptions.length === 0) {
      if (includes.includes('internal')) {
        internalResponse.refetch()
      }
      if (includes.includes('external')) {
        externalResponse.refetch()
      }
    }
    // Only run once on mount - dependencies intentionally omitted
  }, [])

  useEffect(() => {
    // 收集未匹配的id
    if (props.value && remoteSearch) {
      if (Array.isArray(props.value)) {
        const internal = props.value.filter(
          (id) =>
            id > 0 &&
            userOptions.every((option) => option.value !== id) &&
            !errorIds.current.includes(id)
        )
        const external = props.value.filter(
          (id) =>
            id < 0 &&
            userOptions.every((option) => option.value !== id) &&
            !errorIds.current.includes(id)
        )
        // 排除错误的id
        if (internal.length > 0) {
          setInternalIds(internal)
        }
        if (external.length > 0) {
          setExternalIds(external)
        }
      } else {
        if (userOptions.every((option) => option.value !== props.value)) {
          if (props.value > 0 && !errorIds.current.includes(props.value)) {
            setInternalIds([props.value])
          } else if (
            props.value < 0 &&
            !errorIds.current.includes(props.value)
          ) {
            setExternalIds([props.value])
          }
        }
      }
    }
  }, [props.value, remoteSearch, userOptions])

  useEffect(() => {
    // 出现未匹配的id时，重新请求数据
    if (includes.includes('internal') && internalIds.length > 0) {
      internalResponse.refetch()
    }
    if (includes.includes('external') && externalIds.length > 0) {
      externalResponse.refetch()
    }
  }, [internalIds, externalIds, includes, internalResponse, externalResponse])

  const handleChange = (
    value: number | number[],
    option: DefaultOptionType
  ) => {
    // 有搜索时，重置search和queryKey
    if (search || queryKey) {
      setSearch('')
      setQueryKey('')
    }

    // console.log('handle change', value, userOptions)
    const arrayValue = value ? (Array.isArray(value) ? value : [value]) : []
    const newUsers = (userOptions || [])
      .filter((option) => arrayValue.includes(option.user.id))
      .map((option) => option.user)

    setSelectedUsers(newUsers)
    onChange?.(value, option)
  }

  // console.log('userOptions', userOptions)
  // console.log('selectedUsers', selectedUsers)

  return (
    <KBSelect
      labelRender={(LabelInValueType) => {
        const currentUser = userOptions?.find(
          (item) => item?.value === LabelInValueType?.value
        )?.user
        if (currentUser) {
          return (
            <KBUser
              size={isMultiple ? 16 : 24}
              showTooltip={false}
              gapNumber={isMultiple ? 4 : 8}
              user={currentUser}
              showName
            />
          )
        } else {
          return LabelInValueType?.value
        }
      }}
      placeholder={placeholder}
      searchInputPlaceholder={t('common.searchNameEmail')}
      value={selectedUsers.map((user) => user.id)}
      showSearchInput
      remoteSearchLoading={
        internalResponse?.isLoading || externalResponse?.isLoading
      }
      searchFn={(option, searchValue) => {
        if (remoteSearch) return false

        const user = userOptions.find(
          (user) => user.id === Math.abs(option.value as number)
        )?.user

        return (
          user?.name?.toLocaleLowerCase().includes(searchValue) ||
          user?.email?.toLocaleLowerCase().includes(searchValue) ||
          (enableDeliveryNicknameSearch &&
            (user as UserEntity)?.delivery_nickname
              ?.toLocaleLowerCase()
              .includes(searchValue)) || // 新增对 delivery_nickname 的查询
          false
        )
      }}
      remoteSearch={remoteSearch}
      remoteSearchFn={handleRemoteSearch}
      options={userOptions}
      tagRender={tagRender}
      className={cn('tw-w-[200px]', className)}
      mode={mode}
      allowClear={allowClear}
      onChange={handleChange}
      // suffixIcon={null}
      {...otherProps}
    ></KBSelect>
  )
}
